import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { intelligenceApi } from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Props = { basePath?: string };

const TABS = [
	{ id: "kpi-targets", label: "KPI Targets" },
	{ id: "attention", label: "Attention Rules" },
	{ id: "insights", label: "Insight Rules" },
	{ id: "leakage", label: "Leakage Rules" },
	{ id: "peer-groups", label: "Benchmark Peer Groups" },
	{ id: "audit", label: "Audit Trail" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function IntelligenceSettingsPage({ basePath }: Props) {
	const navBase = basePath ?? "/hospital-admin";
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isChainScoped = isChainAdmin;
	const chainHospitals = user?.hospitals ?? [];
	const qc = useQueryClient();

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [tab, setTab] = useState<TabId>("kpi-targets");
	const [selectedHospitalId, setSelectedHospitalId] = useState("");
	const [kpiForm, setKpiForm] = useState({
		kpiKey: "net_revenue",
		periodKey: new Date().toISOString().slice(0, 7),
		targetValue: "",
	});
	const [peerForm, setPeerForm] = useState({ name: "", hospitalIds: "" });

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-settings"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		return selectedHospitalId
			? [{ id: selectedHospitalId, name: "Current hospital" }]
			: [];
	}, [isSuperAdmin, superHospitals, isChainScoped, chainHospitals, selectedHospitalId]);

	const scopeParams = useMemo(
		() => ({ hospitalId: selectedHospitalId || undefined }),
		[selectedHospitalId]
	);

	const kpiQuery = useQuery({
		queryKey: ["intelligence-settings-kpi", scopeParams],
		queryFn: () => intelligenceApi.getKpiTargetSettings(scopeParams),
		enabled: tab === "kpi-targets" && !!selectedHospitalId,
	});

	const attentionQuery = useQuery({
		queryKey: ["intelligence-settings-attention", scopeParams],
		queryFn: () => intelligenceApi.getAttentionRuleSettings(scopeParams),
		enabled: tab === "attention" && !!selectedHospitalId,
	});

	const insightQuery = useQuery({
		queryKey: ["intelligence-settings-insight", scopeParams],
		queryFn: () => intelligenceApi.getInsightRuleSettings(scopeParams),
		enabled: tab === "insights" && !!selectedHospitalId,
	});

	const leakageQuery = useQuery({
		queryKey: ["intelligence-settings-leakage", scopeParams],
		queryFn: () => intelligenceApi.getLeakageRuleSettings(scopeParams),
		enabled: tab === "leakage" && !!selectedHospitalId,
	});

	const peerQuery = useQuery({
		queryKey: ["intelligence-settings-peers", scopeParams],
		queryFn: () => intelligenceApi.getBenchmarkGroups(scopeParams),
		enabled: tab === "peer-groups",
	});

	const auditQuery = useQuery({
		queryKey: ["intelligence-settings-audit", scopeParams, tab],
		queryFn: () =>
			intelligenceApi.getConfigurationAudit({
				hospitalId: scopeParams.hospitalId,
				pageSize: 50,
			}),
		enabled: tab === "audit",
	});

	const createKpiMut = useMutation({
		mutationFn: () =>
			intelligenceApi.createKpiTarget({
				...scopeParams,
				kpiKey: kpiForm.kpiKey,
				periodKey: kpiForm.periodKey,
				targetValue: Number(kpiForm.targetValue),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["intelligence-settings-kpi"] });
			setKpiForm((f) => ({ ...f, targetValue: "" }));
		},
	});

	const patchInsightMut = useMutation({
		mutationFn: ({ ruleKey, enabled }: { ruleKey: string; enabled: boolean }) =>
			intelligenceApi.patchInsightRuleSetting(ruleKey, { ...scopeParams, enabled }),
		onSuccess: () => void qc.invalidateQueries({ queryKey: ["intelligence-settings-insight"] }),
	});

	const patchLeakageMut = useMutation({
		mutationFn: ({ ruleKey, enabled }: { ruleKey: string; enabled: boolean }) =>
			intelligenceApi.patchLeakageRuleSetting(ruleKey, { ...scopeParams, enabled }),
		onSuccess: () => void qc.invalidateQueries({ queryKey: ["intelligence-settings-leakage"] }),
	});

	const createPeerMut = useMutation({
		mutationFn: () =>
			intelligenceApi.createBenchmarkGroup({
				name: peerForm.name,
				hospitalIds: peerForm.hospitalIds.split(",").map((s) => s.trim()).filter(Boolean),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["intelligence-settings-peers"] });
			setPeerForm({ name: "", hospitalIds: "" });
		},
	});

	return (
		<div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6">
			<IntelligenceSubNav basePath={navBase} />

			<div>
				<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
					<Settings className="h-7 w-7 text-indigo-600" />
					Intelligence Settings
				</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Configure KPI targets, rule thresholds, and benchmark peer groups. Hospital overrides take
					precedence over group and global defaults.
				</p>
			</div>

			{(isSuperAdmin || isChainScoped) && (
				<div className="flex flex-wrap items-center gap-2">
					<label className="text-sm text-muted-foreground">Hospital scope</label>
					<select
						value={selectedHospitalId}
						onChange={(e) => setSelectedHospitalId(e.target.value)}
						className="rounded-md border border-input bg-background px-3 py-1.5 text-sm min-w-[200px]"
					>
						<option value="">Select hospital…</option>
						{hospitalOptions.map((h) => (
							<option key={h.id} value={h.id}>
								{h.name}
							</option>
						))}
					</select>
				</div>
			)}

			<div className="flex flex-wrap gap-2 border-b pb-3">
				{TABS.map((t) => (
					<button
						key={t.id}
						type="button"
						onClick={() => setTab(t.id)}
						className={`rounded-md px-3 py-1.5 text-sm font-medium ${
							tab === t.id
								? "bg-indigo-100 text-indigo-900"
								: "text-muted-foreground hover:bg-muted"
						}`}
					>
						{t.label}
					</button>
				))}
			</div>

			{tab !== "peer-groups" && !selectedHospitalId && (
				<Card>
					<CardContent className="pt-4 text-sm text-muted-foreground">
						Select a hospital to view and edit settings for that scope.
					</CardContent>
				</Card>
			)}

			{tab === "kpi-targets" && selectedHospitalId && (
				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Add KPI target</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-wrap gap-2 items-end">
							<select
								value={kpiForm.kpiKey}
								onChange={(e) => setKpiForm((f) => ({ ...f, kpiKey: e.target.value }))}
								className="rounded-md border px-2 py-1.5 text-sm"
							>
								{(kpiQuery.data?.catalog ?? []).map((k) => (
									<option key={k.key} value={k.key}>
										{k.name}
									</option>
								))}
							</select>
							<input
								type="month"
								value={kpiForm.periodKey}
								onChange={(e) => setKpiForm((f) => ({ ...f, periodKey: e.target.value }))}
								className="rounded-md border px-2 py-1.5 text-sm"
							/>
							<input
								type="number"
								placeholder="Target value"
								value={kpiForm.targetValue}
								onChange={(e) => setKpiForm((f) => ({ ...f, targetValue: e.target.value }))}
								className="rounded-md border px-2 py-1.5 text-sm w-36"
							/>
							<Button
								size="sm"
								disabled={!kpiForm.targetValue || createKpiMut.isPending}
								onClick={() => createKpiMut.mutate()}
							>
								Save target
							</Button>
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Configured targets</CardTitle>
						</CardHeader>
						<CardContent>
							{kpiQuery.isLoading && (
								<div className="flex gap-2 text-muted-foreground text-sm">
									<Loader2 className="h-4 w-4 animate-spin" /> Loading…
								</div>
							)}
							<ul className="space-y-2 text-sm">
								{(kpiQuery.data?.targets ?? []).map((t) => (
									<li key={t.id} className="flex justify-between border-b pb-2">
										<span>
											{t.kpiKey} · {t.periodKey} · scope {t.scopeKey}
										</span>
										<span className="font-medium tabular-nums">
											{t.targetValue.toLocaleString("en-IN")} {t.active ? "" : "(inactive)"}
										</span>
									</li>
								))}
							</ul>
						</CardContent>
					</Card>
				</div>
			)}

			{tab === "attention" && selectedHospitalId && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Management attention thresholds</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-sm">
						{attentionQuery.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
						{(attentionQuery.data?.rules ?? []).slice(0, 12).map((r) => (
							<div key={r.ruleKey} className="flex justify-between gap-4 border-b pb-2">
								<span>{r.name}</span>
								<span className="text-muted-foreground tabular-nums">
									default {r.defaultThreshold}
									{r.override ? ` · override ${r.override.threshold}` : ""}
								</span>
							</div>
						))}
						<p className="text-xs text-muted-foreground pt-2">
							Use PATCH API or extend this UI to edit thresholds per rule.
						</p>
					</CardContent>
				</Card>
			)}

			{tab === "insights" && selectedHospitalId && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Insight rules</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-sm">
						{(insightQuery.data?.rules ?? []).map((r) => (
							<div key={r.ruleKey} className="flex items-center justify-between gap-4 border-b pb-2">
								<span>{r.name}</span>
								<Button
									size="sm"
									variant="outline"
									onClick={() =>
										patchInsightMut.mutate({
											ruleKey: r.ruleKey,
											enabled: !(r.override?.enabled ?? r.defaults.enabled),
										})
									}
								>
									{(r.override?.enabled ?? r.defaults.enabled) ? "Disable" : "Enable"}
								</Button>
							</div>
						))}
					</CardContent>
				</Card>
			)}

			{tab === "leakage" && selectedHospitalId && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Leakage rules</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-sm">
						{(leakageQuery.data?.rules ?? []).map((r) => (
							<div key={r.ruleKey} className="flex items-center justify-between gap-4 border-b pb-2">
								<span>{r.name}</span>
								<Button
									size="sm"
									variant="outline"
									onClick={() =>
										patchLeakageMut.mutate({
											ruleKey: r.ruleKey,
											enabled: !(r.override?.enabled ?? r.defaults.enabled),
										})
									}
								>
									{(r.override?.enabled ?? r.defaults.enabled) ? "Disable" : "Enable"}
								</Button>
							</div>
						))}
					</CardContent>
				</Card>
			)}

			{tab === "peer-groups" && (
				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Create peer group (min 3 hospitals)</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							<input
								placeholder="Group name"
								value={peerForm.name}
								onChange={(e) => setPeerForm((f) => ({ ...f, name: e.target.value }))}
								className="rounded-md border px-2 py-1.5 text-sm w-full max-w-md"
							/>
							<input
								placeholder="Hospital IDs (comma-separated)"
								value={peerForm.hospitalIds}
								onChange={(e) => setPeerForm((f) => ({ ...f, hospitalIds: e.target.value }))}
								className="rounded-md border px-2 py-1.5 text-sm w-full"
							/>
							<Button
								size="sm"
								disabled={!peerForm.name || createPeerMut.isPending}
								onClick={() => createPeerMut.mutate()}
							>
								Create group
							</Button>
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Peer groups</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 text-sm">
							{(peerQuery.data?.groups ?? []).map((g) => (
								<div key={g.id} className="border rounded-md p-3">
									<p className="font-medium">{g.name}</p>
									<p className="text-muted-foreground text-xs mt-1">
										{g.members.map((m) => m.hospital.name).join(", ")}
									</p>
								</div>
							))}
						</CardContent>
					</Card>
				</div>
			)}

			{tab === "audit" && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Configuration audit trail</CardTitle>
					</CardHeader>
					<CardContent>
						{auditQuery.isLoading && (
							<div className="flex gap-2 text-muted-foreground text-sm">
								<Loader2 className="h-4 w-4 animate-spin" /> Loading audit log…
							</div>
						)}
						{auditQuery.data && (
							<>
								<p className="text-xs text-muted-foreground mb-3">
									{auditQuery.data.pagination.total} change
									{auditQuery.data.pagination.total === 1 ? "" : "s"} recorded (append-only).
								</p>
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b text-left text-muted-foreground">
												<th className="py-2 pr-3">When</th>
												<th className="py-2 pr-3">Type</th>
												<th className="py-2 pr-3">Key</th>
												<th className="py-2 pr-3">Actor</th>
												<th className="py-2">Reason</th>
											</tr>
										</thead>
										<tbody>
											{auditQuery.data.entries.length === 0 && (
												<tr>
													<td colSpan={5} className="py-6 text-center text-muted-foreground">
														No configuration changes logged yet.
													</td>
												</tr>
											)}
											{auditQuery.data.entries.map((e) => (
												<tr key={e.id} className="border-b last:border-0">
													<td className="py-2 pr-3 whitespace-nowrap tabular-nums">
														{new Date(e.createdAt).toLocaleString("en-IN")}
													</td>
													<td className="py-2 pr-3">{e.settingType}</td>
													<td className="py-2 pr-3 font-mono text-xs">{e.settingKey}</td>
													<td className="py-2 pr-3 text-xs">
														{e.actorRole}
														{e.hospitalId ? ` · ${e.hospitalId.slice(0, 8)}…` : ""}
													</td>
													<td className="py-2 text-muted-foreground">{e.reason ?? "—"}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
